import type { FanSignup, FanSignupSource } from './fanSignups';

export type CampaignChannel = 'email' | 'sms';
export type CampaignStatus = 'draft' | 'scheduled' | 'sent';
export type CampaignTrigger = 'manual' | 'new-show' | 'new-merch' | 'new-release' | 'welcome';

export interface CampaignAudience {
  source: 'all' | FanSignupSource;
  tag: 'all' | string;
}

export interface FanCampaign {
  id: string;
  name: string;
  trigger: CampaignTrigger;
  triggerReference?: string;
  channel: CampaignChannel;
  audience: CampaignAudience;
  subject: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  status: CampaignStatus;
  createdAt: string;
  scheduledAt?: string;
  sentAt?: string;
}

export const campaignsStorageKey = 'artistos-fan-campaigns';
export const campaignsUpdatedEvent = 'artistos:fan-campaigns-updated';

export function getFanCampaigns(): FanCampaign[] {
  try {
    const stored = window.localStorage.getItem(campaignsStorageKey);
    const campaigns = stored ? JSON.parse(stored) : [];
    return Array.isArray(campaigns) ? campaigns.filter(isFanCampaign) : [];
  } catch {
    return [];
  }
}

export function saveFanCampaign(campaign: FanCampaign): boolean {
  try {
    const existing = getFanCampaigns();
    const index = existing.findIndex((item) => item.id === campaign.id);
    const updated = index === -1 ? [campaign, ...existing] : existing.map((item) => item.id === campaign.id ? campaign : item);
    persistCampaigns(updated);
    return true;
  } catch {
    return false;
  }
}

export function createFanCampaign(campaign: Omit<FanCampaign, 'id' | 'createdAt'>): FanCampaign | null {
  const record: FanCampaign = { ...campaign, id: crypto.randomUUID(), createdAt: new Date().toISOString() };
  return saveFanCampaign(record) ? record : null;
}

export function getEligibleFans(fans: FanSignup[], campaign: Pick<FanCampaign, 'channel' | 'audience'>): FanSignup[] {
  return fans.filter((fan) => {
    if (campaign.channel === 'sms' && (!fan.smsOptIn || !fan.phone)) return false;
    if (campaign.audience.source !== 'all' && fan.source !== campaign.audience.source) return false;
    if (campaign.audience.tag !== 'all' && !fan.tags.some((tag) => tag.toLowerCase() === campaign.audience.tag.toLowerCase())) return false;
    return Boolean(fan.email);
  });
}

export function createShowCampaignDraft(show: { id: string; date: string; venue: string; city: string; ticketUrl: string; presaleNote: string }) {
  return createAutoDraft(`show:${show.id}`, {
    name: `${show.venue} · fan announcement`,
    trigger: 'new-show',
    channel: 'email',
    audience: { source: 'all', tag: 'VIP' },
    subject: `First dibs: ${show.venue} is on the calendar`,
    body: `Hey {{first_name}},\n\nWe’re headed to ${show.venue} in ${show.city} on ${show.date}. ${show.presaleNote || 'Tickets are ready when you are.'}\n\nYou’re hearing it here first—grab your spot and we’ll see you out there.`,
    ctaLabel: 'Get tickets',
    ctaUrl: show.ticketUrl,
    status: 'draft',
  });
}

export function createMerchCampaignDraft(item: { id: string; name: string; description: string; price: number; checkoutUrl: string }, fallbackCheckoutUrl: string) {
  return createAutoDraft(`merch:${item.id}`, {
    name: `${item.name} · merch drop`,
    trigger: 'new-merch',
    channel: 'email',
    audience: { source: 'all', tag: 'VIP' },
    subject: `New from the road: ${item.name}`,
    body: `Hey {{first_name}},\n\n${item.name} just landed in the store. ${item.description}\n\nIt’s $${item.price.toFixed(2)} while it lasts.`,
    ctaLabel: 'Shop the drop',
    ctaUrl: item.checkoutUrl || fallbackCheckoutUrl,
    status: 'draft',
  });
}

function createAutoDraft(triggerReference: string, campaign: Omit<FanCampaign, 'id' | 'createdAt' | 'triggerReference'>) {
  if (getFanCampaigns().some((item) => item.triggerReference === triggerReference)) return null;
  return createFanCampaign({ ...campaign, triggerReference });
}

function persistCampaigns(campaigns: FanCampaign[]) {
  window.localStorage.setItem(campaignsStorageKey, JSON.stringify(campaigns));
  window.dispatchEvent(new CustomEvent(campaignsUpdatedEvent, { detail: campaigns }));
}

function isFanCampaign(value: unknown): value is FanCampaign {
  const campaign = value as Partial<FanCampaign>;
  return typeof campaign?.id === 'string' && typeof campaign.name === 'string' && typeof campaign.channel === 'string' && typeof campaign.status === 'string';
}
